/**
 * useInlineEventTime.js
 *
 * FT-284: reusable hook for the inline event-time field used by every
 * in-voyage data modal (Position, Weather, Fuel, Note, Operation Start)
 * and — coming in FT-283 — the Operation Complete confirm.
 *
 * Decouples "when did this actually happen" (eventTime) from "when did the
 * Master click the button" (recordedAtDevice). The data modal calls this hook
 * UNCONDITIONALLY at the top of its component (Rules-of-Hooks safe), mounts
 * <InlineTimeField>, and on submit calls `timeField.freshSnapshot()` to
 * re-capture "now" at the exact tap moment.
 *
 * Public API (FROZEN once FT-283 lands — see FT-284 brief §7):
 *
 *   {
 *     // Setters
 *     setEditing(boolean),
 *     setDateTime(localDate, localTime),
 *     setReasonText(string),
 *     setReasonCode(string),
 *     setReasonOtherNote(string),
 *     reset(),
 *
 *     // State
 *     state: 'now' | 'editing',
 *     band: 'none' | 'free_text' | 'structured',
 *     isFuture: boolean,
 *     isValid: boolean,
 *     errorMessage: string | null,
 *     eventTime: string,         // ISO
 *     eventTimezone: string,
 *     delayReason: { code, note? } | undefined,
 *
 *     // Critical for submit-time correctness
 *     freshSnapshot(): { eventTime, eventTimezone, delayReason }
 *   }
 *
 * Settled-now ref pattern (see §7): when state==='now' the displayed eventTime
 * is read from settledNowRef.current — captured ONCE in the useRef initializer
 * (StrictMode-safe). freshSnapshot() re-stamps the ref at submit time so the
 * audit ISO is fresh even if the modal was open for >5 minutes.
 */

import { useRef, useState, useCallback, useMemo } from 'react';
import {
  getOffsetForTimezone,
  localTimeToUtcIso
} from '../../../../../utils/timezoneUtils';
import { computeBackdatingBand } from './TimePickerModal';

export function useInlineEventTime(vesselTimezone) {
  const tz = vesselTimezone || 'UTC';

  // Captured ONCE in the ref initializer. React evaluates this exactly once
  // per mount even under StrictMode dev-time double-invoke. Submit-time
  // freshSnapshot() re-captures regardless.
  const settledNowRef = useRef(new Date().toISOString());

  const [state, setState] = useState('now'); // 'now' | 'editing'
  const [localDate, setLocalDate] = useState('');
  const [localTime, setLocalTime] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [reasonOtherNote, setReasonOtherNote] = useState('');

  // Derived edited ISO from inputs (only meaningful while editing).
  const editedEventTimeIso = useMemo(() => {
    if (state !== 'editing' || !localDate || !localTime) return null;
    try {
      return localTimeToUtcIso(localDate, localTime, tz);
    } catch {
      return null;
    }
  }, [state, localDate, localTime, tz]);

  // Delta between edited time and "now". Only computed while editing.
  const editedDeltaMs = useMemo(() => {
    if (!editedEventTimeIso) return 0;
    const ms = Date.now() - new Date(editedEventTimeIso).getTime();
    return Number.isFinite(ms) ? ms : 0;
  }, [editedEventTimeIso]);

  const isFuture = state === 'editing'
    && editedEventTimeIso !== null
    && editedDeltaMs < 0;

  const band = state === 'now'
    ? 'none'
    : computeBackdatingBand(Math.max(0, editedDeltaMs));

  // Build delayReason object from band + form state.
  const delayReason = useMemo(() => {
    if (state === 'now' || band === 'none') return undefined;
    if (band === 'free_text') {
      return reasonText.trim().length >= 3
        ? { code: 'free_text', note: reasonText.trim() }
        : undefined;
    }
    // band === 'structured'
    if (!reasonCode) return undefined;
    if (reasonCode === 'other') {
      return reasonOtherNote.trim().length >= 3
        ? { code: 'other', note: reasonOtherNote.trim() }
        : undefined;
    }
    return { code: reasonCode };
  }, [state, band, reasonText, reasonCode, reasonOtherNote]);

  const isValid =
    state === 'now'
    || (
      state === 'editing'
      && editedEventTimeIso !== null
      && !isFuture
      && (band === 'none' || delayReason !== undefined)
    );

  const errorMessage = useMemo(() => {
    if (state === 'now') return null;
    if (isFuture) return 'Event time cannot be in the future.';
    if (band !== 'none' && delayReason === undefined) {
      return band === 'free_text'
        ? 'Reason required (≥3 characters).'
        : 'Select a reason for the delay.';
    }
    return null;
  }, [state, isFuture, band, delayReason]);

  // Re-snapshot at submit. Read settledNowRef.current DIRECTLY in the body —
  // ref mutations don't trigger re-renders so deps would create a stale
  // closure. eventTime is also intentionally NOT in deps (derived from the
  // same sources already listed).
  const freshSnapshot = useCallback(() => {
    if (state === 'now') {
      const fresh = new Date().toISOString();
      settledNowRef.current = fresh;
      return {
        eventTime: fresh,
        eventTimezone: getOffsetForTimezone(tz, new Date(fresh)),
        delayReason: undefined
      };
    }
    return {
      eventTime: editedEventTimeIso,
      eventTimezone: getOffsetForTimezone(tz, new Date(editedEventTimeIso)),
      delayReason
    };
  }, [state, tz, editedEventTimeIso, delayReason]);

  const reset = useCallback(() => {
    settledNowRef.current = new Date().toISOString();
    setState('now');
    setLocalDate('');
    setLocalTime('');
    setReasonCode('');
    setReasonText('');
    setReasonOtherNote('');
  }, []);

  // Returned eventTime for display: settled-now ref while 'now', edited ISO
  // while 'editing'.
  const eventTime = state === 'now' ? settledNowRef.current : editedEventTimeIso;

  // Returned eventTimezone is the resolved offset label (e.g. "+01:00") for
  // audit completeness. Falls back to the raw tz string if formatting fails.
  let eventTimezone;
  try {
    eventTimezone = eventTime
      ? getOffsetForTimezone(tz, new Date(eventTime))
      : tz;
  } catch {
    eventTimezone = tz;
  }

  return {
    // Setters
    setEditing: useCallback((next) => setState(next ? 'editing' : 'now'), []),
    setDateTime: useCallback((d, t) => { setLocalDate(d); setLocalTime(t); }, []),
    setReasonCode,
    setReasonText,
    setReasonOtherNote,
    reset,

    // State
    state,
    band,
    isFuture,
    isValid,
    errorMessage,
    eventTime,
    eventTimezone,
    delayReason,

    // Raw inputs (for the field component)
    localDate,
    localTime,
    reasonCode,
    reasonText,
    reasonOtherNote,
    tz,

    // Critical for submit-time correctness
    freshSnapshot
  };
}

export default useInlineEventTime;
